import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../services/api";
import { Student, defaultStudent } from "../types/Student";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import { GRADE_FILTER_OPTIONS } from "../components/Grade";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import RoundBox from "../components/RoundBox";
import RoundBoxInput from "../components/RoundBoxInput";
import RoundBoxSelect from "../components/RoundBoxSelect";

function StudentCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState<Student>(defaultStudent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [classes, setClasses] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // 로드
  useEffect(() => {
    const load = async () => {
      try {
        // 클래스 목록 항상 로드 (정규반, 특강반만)
        const classesData = await apiClient.getClasses();
        const eligibleClasses = classesData.filter(
          (c: any) => c.classCategory === 'regular' || c.classCategory === 'special'
        );
        setClasses(eligibleClasses);

        // 편집 모드면 학생 정보 로드
        if (!isNew) {
          setLoading(true);
          const data = await apiClient.getStudent(Number(id));
          setForm(data);
        }
      } catch (e: any) {
        setError(e?.message || "로드 실패");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const { id: sid, classes: studentClasses, ...payload } = form;
      
      // classIds 배열 생성
      const classIds = (studentClasses || []).map((c: any) => c.id).filter(Boolean);
      
      const dataToSend = { ...payload, classIds };
      
      if (isNew) {
        await apiClient.createStudent(dataToSend);
      } else {
        await apiClient.updateStudent(String(sid), dataToSend);
      }
      navigate("/student");
    } catch (e: any) {
      setError(e?.message || "저장 실패");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (isNew || !form.id) return;
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await apiClient.deleteStudent(String(form.id));
      navigate("/student");
    } catch (e: any) {
      setError(e?.message || "삭제 실패");
    } finally {
      setLoading(false);
    }
  };

  // 반 선택/해제 핸들러
  const handleClassToggle = (classItem: any) => {
    const currentClasses = form.classes || [];
    const isSelected = currentClasses.some((c: any) => c.id === classItem.id);
    
    if (isSelected) {
      setForm((f) => ({
        ...f,
        classes: currentClasses.filter((c: any) => c.id !== classItem.id),
      }));
    } else {
      setForm((f) => ({
        ...f,
        classes: [...currentClasses, classItem],
      }));
    }
  };

  return (
    <div style={{ padding: "16px 2.5%" }}>
      <h2>학생 카드</h2>

      {/* 폼 박스 */}
      <RoundBox label={isNew ? "학생 추가" : "학생 정보"} direction="vertical" style={{ maxWidth: 720 }}>
        <RoundBoxInput
          label="출결번호"
          value={form.studentNumber}
          onChange={(e) => setForm((f) => ({ ...f, studentNumber: e.target.value }))}
        />
        <RoundBoxInput
          label="이름"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <RoundBoxSelect
          label="학년"
          value={form.grade}
          onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
          options={GRADE_FILTER_OPTIONS}
        />
        <RoundBoxInput
          label="전화번호"
          value={form.phone ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
        />
        <RoundBoxInput
          label="메시지 수신자"
          value={form.messageRecipient ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, messageRecipient: e.target.value }))}
        />
        <RoundBoxInput
          label="메시지 수신번호"
          value={form.messagePhone ?? ""}
          onChange={(e) => setForm((f) => ({ ...f, messagePhone: e.target.value }))}
        />
        
        {/* 반 선택 (다중 선택 체크박스) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontWeight: 600, fontSize: 14 }}>반</label>
          <div style={{
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            padding: 12,
            maxHeight: 300,
            overflowY: "auto",
          }}>
            {classes.length === 0 ? (
              <div style={{ color: "#999", fontSize: 14 }}>등록 가능한 반이 없습니다.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {classes.map((classItem) => {
                  const isSelected = (form.classes || []).some((c: any) => c.id === classItem.id);
                  const category = classItem.classCategory || 'regular';
                  const categoryLabel = category === 'regular' ? '정규반' : '특강반';
                  const categoryColor = category === 'regular' ? '#1976d2' : '#2e7d32';
                  
                  return (
                    <label
                      key={classItem.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: 8,
                        borderRadius: 6,
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#f0f4ff" : "transparent",
                        border: isSelected ? "1px solid #b6d4fe" : "1px solid transparent",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleClassToggle(classItem)}
                        style={{ cursor: "pointer", width: 16, height: 16 }}
                      />
                      <span style={{ flex: 1, fontSize: 14 }}>
                        {classItem.className}
                      </span>
                      <span style={{
                        fontSize: 11,
                        padding: "2px 6px",
                        borderRadius: 4,
                        backgroundColor: category === 'regular' ? '#e3f2fd' : '#e8f5e9',
                        color: categoryColor,
                        fontWeight: 600,
                      }}>
                        {categoryLabel}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div style={{ color: "#d73a49", marginTop: 8 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={handleSave} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.save }}>
            저장
          </button>
          {!isNew && (
            <button onClick={handleDeleteClick} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.delete }}>
              삭제
            </button>
          )}
          <button onClick={() => navigate("/student")} style={{ ...buttonStyle, ...buttonBackgroundColor.cancel }}>
            목록으로
          </button>
        </div>
      </RoundBox>

      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={form.name}
        itemType="학생"
      />
    </div>
  );
}

export default StudentCardPage;
